/* eslint-disable no-console */
/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable indent */
/* eslint-disable quotes */
import React, { useState, useEffect, useRef } from 'react';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { doc, getDoc, setDoc, addDoc, deleteDoc, onSnapshot, collection } from 'firebase/firestore';
import { auth, db, firebaseConfig, persistenceReady, getArtifactProjectId } from '../firebaseInit';
import html2canvas from 'html2canvas';
import { normalizeEmailToDocId } from '../utils/emailNormalization';
import { registerSession } from '../utils/sessionManager';
import { useNotification } from '../contexts/NotificationContext';
import LoadingSpinner from './shared/LoadingSpinner';
import { InlineError } from './shared/ErrorMessage';
import Button from './shared/Button';
import { usePricing } from '../contexts/PricingContext';
import { getIndustryOptions, getIndustryDescription } from '../utils/industryConfig';

const Login = ({ onLogin, onClose, initialMode = 'signin', preventAutoClose = false, embedded = false, adminOverrideKey = null }) => {
  // Detect test environment to restrict self-service signup
  const isTestEnv = firebaseConfig.projectId === 'treegenie-test';
  // Admin override allows signup on test when valid key is provided
  const hasAdminOverride = isTestEnv && !!adminOverrideKey;
  
  // Detect invite token from URL at initialization to skip tier selection on first render
  const urlInviteToken = new URLSearchParams(window.location.search).get('invite');
  // On test env, only allow signup via invitation link or admin override
  const [isSignUp, setIsSignUp] = useState(
    (initialMode === 'signup' && (!isTestEnv || hasAdminOverride)) || !!urlInviteToken
  );
  const [signupTier, setSignupTier] = useState(urlInviteToken ? 'basic' : null);
  const [showPlanSelection, setShowPlanSelection] = useState(false); // New: show pricing tiers for team leads
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('real-estate-investor');
  const [industryDescription, setIndustryDescription] = useState(() => getIndustryDescription('real-estate-investor'));
  const [industryDescriptionEdited, setIndustryDescriptionEdited] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(''); // New: specific loading feedback
  const loginTimestamp = useRef(null); // New: track login timing
  const [invitationValidated, setInvitationValidated] = useState(false);
  const [invitationData, setInvitationData] = useState(null);
  const [teamLeadEmail, setTeamLeadEmail] = useState('');
  const [inviteToken, setInviteToken] = useState(urlInviteToken || null); // Token from invitation link
  const [validatingToken, setValidatingToken] = useState(false); // Loading state for token validation
  
  // Subscription plan selection from URL (e.g., ?plan=starter&interval=year)
  const [selectedPlan, setSelectedPlan] = useState('starter'); // Default to starter
  const [billingInterval, setBillingInterval] = useState('month');
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
    
  // Feedback state
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackDescription, setFeedbackDescription] = useState('');
  const [feedbackType, setFeedbackType] = useState('bug');
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  const [capturedScreenshot, setCapturedScreenshot] = useState(null);
  const [isCapturingData, setIsCapturingData] = useState(false);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);
    
  // Console log tracking for problem reports (using ref to avoid re-renders)
  const consoleHistory = useRef([]);
    
  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [isSubmittingReset, setIsSubmittingReset] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
    
  // Use notification system (replaces alert())
  const { showSuccess, showError, showWarning, showInfo } = useNotification();
  
  // Dynamic pricing from Firestore
  const { plans: PLANS, loading: pricingLoading } = usePricing();
    
  // Detect Safari for special handling
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  useEffect(() => {
    // Read URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const planParam = urlParams.get('plan');
    const intervalParam = urlParams.get('interval');
    const inviteTokenParam = urlParams.get('invite'); // Agent invitation token
    
    // Handle agent invitation link (takes priority)
    if (inviteTokenParam) {
      setInviteToken(inviteTokenParam);
      setIsSignUp(true);
      setSignupTier('basic');
      // Token validation will be triggered by the next useEffect
      return;
    }
    
    // Handle signup mode (from landing page "Get Started" / "Create Account")
    // On test environment, block self-service signup unless admin override is active
    if (initialMode === 'signup' && (!isTestEnv || hasAdminOverride)) {
      setIsSignUp(true);
      
      // Check if a specific plan was selected from pricing cards
      if (planParam && ['starter', 'pro', 'pro_plus'].includes(planParam)) {
        // Plan already selected - go directly to team lead signup form
        setSelectedPlan(planParam);
        setSignupTier('team_lead');
        setShowPlanSelection(false);
      }
      // Otherwise, don't set signupTier — show "Choose Your Account Type"
      // screen so agents can find the agent signup path
    }
    
    // Handle billing interval
    if (intervalParam && ['month', 'year'].includes(intervalParam)) {
      setBillingInterval(intervalParam);
    }
        
    // Clean up any existing password reset cooldown from localStorage
    localStorage.removeItem('passwordResetCooldown');
  }, [initialMode]);

  // Validate invite token when it's set
  useEffect(() => {
    if (inviteToken && !invitationValidated && !validatingToken) {
      handleAgentInviteLink(inviteToken);
    }
  }, [inviteToken, invitationValidated, validatingToken]);

  // Set up console log tracking for problem reports
  useEffect(() => {
    const originalConsole = {
      log: console.log,
      error: console.error,
      warn: console.warn,
      info: console.info
    };

    // More precise patterns - only redact actual sensitive values, not words
    const SENSITIVE_VALUE_PATTERNS = [
      // Actual passwords (after colons, equals, etc.)
      /(?:password|pwd|pass)\s*[:=]\s*["']?([^"'\s,}]+)["']?/gi,
      // API keys and tokens (after colons, equals, etc.)
      /(?:api[_-]?key|token|secret|bearer|jwt)\s*[:=]\s*["']?([^"'\s,}]+)["']?/gi,
      // Session IDs and cookies (after colons, equals, etc.)
      /(?:session|sessionid|cookie|credential)\s*[:=]\s*["']?([^"'\s,}]+)["']?/gi,
      // Email addresses (but preserve error codes like auth/user-not-found)
      /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g,
      // Long base64 strings that look like tokens (20+ chars, but not error codes)
      /(?:^|[^\/\w-])[A-Za-z0-9+/]{24,}={0,2}(?=[^\/\w-]|$)/g,
      // UUIDs (but preserve Firebase error codes)
      /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi,
    ];

    // Patterns to completely ignore (too noisy or not useful)
    const IGNORE_PATTERNS = [
      /React DevTools/i,
      /Download the React DevTools/i,
      /Warning: React/i,
      /console\.debug/i,
      /HMR/i,
      /Hot Module Replacement/i,
      /webpack/i,
      /chunk/i,
      /sourcemap/i
    ];

    const sanitizeMessage = (message) => {
      let sanitized = String(message);
            
      // Check if message should be completely ignored
      for (const pattern of IGNORE_PATTERNS) {
        if (pattern.test(sanitized)) {
          return null; // Skip this message entirely
        }
      }
            
      // Replace only actual sensitive values, preserving context
      for (const pattern of SENSITIVE_VALUE_PATTERNS) {
        sanitized = sanitized.replace(pattern, (match, capturedValue) => {
          // If there's a captured group (the actual value), redact only that part
          if (capturedValue) {
            return match.replace(capturedValue, '[REDACTED]');
          }
          // Otherwise redact the whole match (for patterns without groups)
          return '[REDACTED]';
        });
      }
            
      // Preserve important debugging context but redact specific sensitive patterns
      // Keep Firebase error codes like "auth/user-not-found", "auth/wrong-password"
      // Keep function names like "validateInvitation", "handleSubmit"
      // Keep general error messages like "Authentication failed", "Network error"
            
      // Limit message length but keep it reasonable for debugging
      if (sanitized.length > 500) {
        sanitized = sanitized.substring(0, 500) + '...[TRUNCATED]';
      }
            
      return sanitized;
    };

    const addToHistory = (level, args) => {
      const rawMessage = args.join(' ');
      const sanitizedMessage = sanitizeMessage(rawMessage);
            
      // Skip if message was filtered out
      if (!sanitizedMessage) return;
            
      const logEntry = {
        level,
        message: sanitizedMessage,
        timestamp: new Date().toISOString()
      };
            
      // Use ref to avoid triggering re-renders and cross-component state updates
      consoleHistory.current.push(logEntry);
      // Keep last 25 entries for login page
      if (consoleHistory.current.length > 25) {
        consoleHistory.current = consoleHistory.current.slice(-25);
      }
    };

    // Override console methods
    console.log = (...args) => {
      originalConsole.log(...args);
      addToHistory('log', args);
    };

    console.error = (...args) => {
      originalConsole.error(...args);
      addToHistory('error', args);
    };

    console.warn = (...args) => {
      originalConsole.warn(...args);
      addToHistory('warn', args);
    };

    console.info = (...args) => {
      originalConsole.info(...args);
      addToHistory('info', args);
    };

    // Cleanup function to restore original console methods
    return () => {
      console.log = originalConsole.log;
      console.error = originalConsole.error;
      console.warn = originalConsole.warn;
      console.info = originalConsole.info;
    };
  }, []);

  // Helper function to create a safe document ID from email
  const getEmailDocumentId = (email) => {
    if (!email) return null;
    return normalizeEmailToDocId(email);
  };


  // Helper function removed - not used

  // Set up real-time listener for maintenance status
  useEffect(() => {
    try {
      const appId = getArtifactProjectId();
      const maintenanceRef = doc(db, `artifacts/${appId}/global/maintenance`);
            
      const unsubscribe = onSnapshot(maintenanceRef, (doc) => {
        if (doc.exists()) {
          setMaintenanceEnabled(doc.data().enabled || false);
        } else {
          setMaintenanceEnabled(false);
        }
      }, (error) => {
        if (error.code === 'permission-denied') {
          console.log('🔧 No permission to read maintenance status in Login (normal for non-admin users)');
          setMaintenanceEnabled(false);
        } else {
          console.error('Error listening to maintenance status in Login:', error);
          setMaintenanceEnabled(false);
        }
      });

      return () => unsubscribe();
    } catch (error) {
      console.error('Error setting up maintenance listener in Login:', error);
      setMaintenanceEnabled(false);
    }
  }, []);



  // Validate invitation for agents using team lead email
  const validateInvitation = async (email, leadEmail) => {
    if (!email || !leadEmail) return false;
        
    const appId = getArtifactProjectId();
    const emailDocId = getEmailDocumentId(email);

    try {
      // Check the global pending invitations index
      console.log('🔍 Checking global invitations index for:', email);
      const globalInvitationRef = doc(db, `artifacts/${appId}/pendingInvitations`, emailDocId);
      const globalInvitationDoc = await getDoc(globalInvitationRef);
            
      if (globalInvitationDoc.exists()) {
        const invitationData = globalInvitationDoc.data();
        console.log('✅ Found invitation in global index:', invitationData);
                
        // Verify the invitation was sent by the specified team lead
        if (invitationData.status === 'invited' && invitationData.invitedBy === leadEmail) {
          console.log('✅ Invitation validated - user invited by:', invitationData.invitedBy);
                    
          // Use the data from global index - no need to access team container
          setInvitationData({ 
            email: invitationData.email,
            teamId: invitationData.teamId, 
            userId: emailDocId, // This is the document ID in the team container
            status: 'invited',
            invitedBy: invitationData.invitedBy,
            invitedAt: invitationData.invitedAt
          });
          setInvitationValidated(true);
          setError('');
          return true;
        } else if (invitationData.status === 'invited') {
          console.log('⚠️ User is invited but by different person:', invitationData.invitedBy, 'expected:', leadEmail);
          setError('No pending invitation found from the specified team lead. Please verify the team lead email address is correct.');
          return false;
        } else {
          console.log('⚠️ User exists but status is:', invitationData.status);
          setError('Your invitation has already been processed or is no longer valid.');
          return false;
        }
      }
            
      setError('No pending invitation found for this email from the specified team lead. Please verify both email addresses are correct, or ask your team lead to re-send the invitation.');
      return false;
            
    } catch (error) {
      console.error('Error validating invitation:', error);
      if (error.code === 'permission-denied') {
        setError('Unable to validate invitation due to security restrictions. Please verify your email and team lead email are correct, or ask your team lead to re-invite you.');
      } else {
        setError('Error validating invitation. Please try again.');
      }
      return false;
    }
  };

  // Handle agent invitation link with token
  const handleAgentInviteLink = async (token) => {
    if (!token) return;
    
    setValidatingToken(true);
    setLoadingMessage('Validating your invitation...');
    
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const validateToken = httpsCallable(functions, 'validateInvitationToken');
      
      const result = await validateToken({ token });
      
      if (result.data.valid) {
        console.log('✅ Invitation token validated:', result.data);
        
        // Set invitation data
        setInvitationData({
          email: result.data.email,
          teamId: result.data.teamId,
          teamName: result.data.teamName,
          invitedBy: result.data.invitedBy,
          token: token // Store token to mark as used after signup
        });
        
        // Pre-fill email (read-only) and skip to password form
        setEmail(result.data.email);
        setInvitationValidated(true);
        setSignupTier('basic');
        setError('');
      }
    } catch (error) {
      console.error('❌ Token validation failed:', error);
      
      // Show user-friendly error message
      const errorCode = error.code || error.details?.code;
      const errorMessage = error.message || 'Failed to validate invitation';
      
      if (errorCode === 'not-found' || errorMessage.includes('Invalid')) {
        setError('This invitation link is invalid or has expired. Please ask your team lead to send a new invitation.');
      } else if (errorCode === 'failed-precondition' || errorMessage.includes('already')) {
        setError('This invitation has already been used. Please sign in with your existing account.');
      } else if (errorCode === 'already-exists') {
        setError('An account with this email already exists. Please sign in instead.');
      } else {
        setError('Unable to validate invitation. Please try again or contact support.');
      }
      
      // Reset to normal signup flow
      setSignupTier(null);
    } finally {
      setValidatingToken(false);
      setLoadingMessage('');
    }
  };

  // Create team for team lead with subscription
  const createTeamForLead = async (email, teamName, selectedPlan = 'starter') => {
    if (!email || !teamName) return null;
        
    try {
      const appId = getArtifactProjectId();
      const teamId = `team_${Date.now()}`;
      const teamRef = doc(db, `artifacts/${appId}/teams/${teamId}`);
      
      // Import subscription utilities
      const { getInitialSubscription, PLANS } = await import('../utils/usageTracking');
      
      // Get subscription configuration for the selected plan
      const subscription = getInitialSubscription(selectedPlan);
      const planConfig = PLANS[selectedPlan] || PLANS.starter;
      
      await setDoc(teamRef, {
        name: teamName,
        leadEmail: email,
        createdAt: new Date(),
        subscription: subscription
      });
            
      // Copy master example-script template to new team
      try {
        const masterTemplateRef = doc(db, `artifacts/${appId}/global/data/system_templates/example-script`);
        const masterTemplate = await getDoc(masterTemplateRef);
                
        if (masterTemplate.exists()) {
          const teamExampleRef = doc(db, `artifacts/${appId}/teams/${teamId}/templates/example-script`);
          await setDoc(teamExampleRef, {
            ...masterTemplate.data(),
            metadata: {
              ...masterTemplate.data().metadata,
              copiedFrom: 'system_templates',
              copiedAt: new Date(),
              teamId: teamId
            }
          });
          console.log('✅ Copied example-script template to new team');
        } else {
          console.warn('⚠️ Master example-script template not found');
        }
      } catch (error) {
        console.error('❌ Error copying example-script to new team:', error);
        // Don't fail team creation if example-script copy fails
      }
            
      return teamId;
    } catch (error) {
      console.error('Error creating team:', error);
      throw new Error('Failed to create team. Please try again.');
    }
  };

  // Create user document for team lead
  const createTeamLeadUser = async (email, teamId) => {
    if (!email || !teamId) return;
        
    try {
      const appId = getArtifactProjectId();
      const emailDocId = getEmailDocumentId(email);
      // Use team container path instead of legacy path
      const userRef = doc(db, `artifacts/${appId}/teams/${teamId}/users/${emailDocId}`);
            
      await setDoc(userRef, {
        email,
        role: 'team_lead',
        teamId,
        defaultTemplate: 'example-script',
        status: 'active',
        createdAt: new Date(),
        signupCompleted: true
      });
    } catch (error) {
      console.error('Error creating team lead user:', error);
      throw new Error('Failed to create user profile. Please try again.');
    }
  };

  const handleTierSelection = (tier) => {
    setSignupTier(tier);
    setError('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setTeamName('');
    setTeamLeadEmail('');
    setInvitationValidated(false);
    setInvitationData(null);
    
    // For team leads, show plan selection before signup form
    if (tier === 'team_lead') {
      setShowPlanSelection(true);
    }
  };
  
  const handlePlanSelection = (planId) => {
    setSelectedPlan(planId);
    setShowPlanSelection(false); // Move to signup form after plan selection
  };

  const handleBasicEmailValidation = async () => {
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
        
    if (!teamLeadEmail.trim()) {
      setError('Please enter your team lead\'s email address');
      return;
    }
        
    setLoading(true);
    const isValid = await validateInvitation(email, teamLeadEmail.trim());
    setLoading(false);
        
    if (isValid) {
      // Proceed to password form
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Use shared auth instance from firebaseInit (not getAuth()) to ensure consistency
      if (isSignUp) {
        // Validate inputs
        if (!email || !password) {
          setError('Please fill in all fields');
          setLoading(false);
          return;
        }

        if (password !== confirmPassword) {
          setError('Passwords do not match');
          setLoading(false);
          return;
        }

        if (password.length < 6) {
          setError('Password must be at least 6 characters');
          setLoading(false);
          return;
        }

        if (signupTier === 'team_lead' && !teamName.trim()) {
          setError('Please enter a team name');
          setLoading(false);
          return;
        }

        // Validate name fields
        if (!firstName.trim() || !lastName.trim()) {
          setError('Please enter your first and last name');
          setLoading(false);
          return;
        }

        // Validate industry description for team leads
        if (signupTier === 'team_lead' && selectedIndustry === 'other' && !industryDescription.trim()) {
          setError('Please describe your business');
          setLoading(false);
          return;
        }

        // Create user account
        await createUserWithEmailAndPassword(auth, email, password);
                
        // Handle post-signup setup based on tier
        if (signupTier === 'team_lead') {
          // For team leads, create team via secure Cloud Function
          console.log('🔄 Team Lead signup - creating team via Cloud Function...');
                    
          try {
            // Import Cloud Functions
            const { getFunctions, httpsCallable } = await import('firebase/functions');
            const functions = getFunctions();
            const createTeamAccount = httpsCallable(functions, 'createTeamAccount');
                        
            // Call secure Cloud Function (runs with admin privileges)
            const result = await createTeamAccount({
              teamName,
              plan: selectedPlan,
              industryId: selectedIndustry,
              industryDescription: industryDescription.trim() || getIndustryDescription(selectedIndustry),
              firstName: firstName.trim(),
              lastName: lastName.trim(),
              ...(adminOverrideKey ? { adminOverrideKey } : {}),
            });
                        
            console.log('✅ Team created successfully:', result.data);
                        
            // Force token refresh to pick up team_lead claims set by createTeamAccount
            // AuthContext may have already set basic claims, so we need to overwrite
            const currentUser = auth.currentUser;
            if (currentUser) {
              let claimsReady = false;
              let attempts = 0;
              const maxAttempts = 10;
              while (!claimsReady && attempts < maxAttempts) {
                try {
                  const tokenResult = await currentUser.getIdTokenResult(true);
                  if (tokenResult.claims.role === 'team_lead' && tokenResult.claims.teamId) {
                    console.log('🔐 Team lead claims ready:', {
                      role: tokenResult.claims.role,
                      teamId: tokenResult.claims.teamId
                    });
                    claimsReady = true;
                  } else {
                    console.log(`⏳ Waiting for team_lead claims (attempt ${attempts + 1})...`, {
                      role: tokenResult.claims.role,
                      teamId: tokenResult.claims.teamId
                    });
                    await new Promise(r => setTimeout(r, 1000));
                  }
                } catch (refreshErr) {
                  console.warn('⚠️ Token refresh error:', refreshErr.message);
                  await new Promise(r => setTimeout(r, 1000));
                }
                attempts++;
              }
              if (!claimsReady) {
                console.warn('⚠️ Claims not ready after polling, reloading anyway');
              }
            }
            
            window.location.reload();
            setLoading(false);
            return;
          } catch (teamError) {
            console.error('Error creating team:', teamError);
                        
            // Delete the auth account so the user can retry signup with the same email
            try {
              if (auth.currentUser) {
                await auth.currentUser.delete();
              }
            } catch (deleteErr) {
              console.error('Error cleaning up auth account:', deleteErr);
              // Fall back to signing out if delete fails
              await auth.signOut();
            }
                        
            // Show user-friendly error
            const errorMessage = teamError.message || 'Failed to create team. Please try again.';
            setError(errorMessage);
            setLoading(false);
            return;
          }
        } else if (signupTier === 'basic' && invitationData) {
          // For agents, complete signup via Cloud Function with admin privileges
          console.log('🔄 Completing agent signup via Cloud Function...');
                    
          try {
            // Import Cloud Functions
            const { getFunctions, httpsCallable } = await import('firebase/functions');
            const functions = getFunctions();
            const completeAgentSignup = httpsCallable(functions, 'completeAgentSignup');
                        
            // Call secure Cloud Function (runs with admin privileges)
            const result = await completeAgentSignup({
              firstName: firstName.trim(),
              lastName: lastName.trim(),
            });
                        
            console.log('✅ Agent signup completed successfully:', result.data);
            
            // Mark invitation token as used (if signup was via invitation link)
            if (invitationData.token) {
              try {
                const markInvitationUsed = httpsCallable(functions, 'markInvitationUsed');
                await markInvitationUsed({ token: invitationData.token });
                console.log('✅ Invitation token marked as used');
              } catch (markError) {
                // Non-critical error - log but don't fail signup
                console.warn('⚠️ Could not mark invitation as used:', markError.message);
              }
            }
                        
            // Wait for claims to propagate, then reload
            setTimeout(() => {
              window.location.reload();
            }, 2000);
                        
            setLoading(false);
            return;
          } catch (signupError) {
            console.error('❌ Agent signup completion failed:', signupError);
            console.error('❌ Error details:', signupError.code || signupError.message);
                        
            // Sign out the user since signup failed
            await auth.signOut();
                        
            // Show user-friendly error
            const errorMessage = signupError.message || 'Signup failed. Please try again or contact your team lead.';
            setError(errorMessage);
            setLoading(false);
            return;
          }
        }
                
        console.log('✅ User created successfully');
      } else {
        // SIGN IN FLOW
        console.log('🔐 [LOGIN-START] User clicked sign in');
                
        // Safari-specific feedback
        if (isSafari) {
          console.log('🍎 Safari detected - login may take 10-15 seconds');
          setLoadingMessage('Safari detected - this may take a moment...');
        } else {
          setLoadingMessage('Signing you in...');
        }
                
        // Wait for persistence to be set before signing in (critical for Safari)
        console.log('🔐 [LOGIN-FIREBASE] Waiting for auth persistence...');
        try {
          // Wait for persistence with longer timeout for Safari (5s vs 3s)
          const persistenceTimeout = isSafari ? 5000 : 3000;
          await Promise.race([
            persistenceReady,
            new Promise((_, reject) => setTimeout(() => reject(new Error('Persistence timeout')), persistenceTimeout))
          ]);
          console.log('🔐 [LOGIN-FIREBASE] Auth persistence ready');
        } catch (timeoutError) {
          console.warn('⚠️ [LOGIN-FIREBASE] Persistence setup timed out, proceeding anyway:', timeoutError.message);
          if (isSafari) {
            setLoadingMessage('Safari persistence timeout - continuing anyway...');
          }
          // Continue with sign-in even if persistence times out
        }
                
        setLoadingMessage(isSafari ? 'Signing you in (Safari)...' : 'Verifying credentials...');
                
        // Track timing for debugging
        loginTimestamp.current = Date.now();
        console.log('🔐 [LOGIN-FIREBASE] Calling signInWithEmailAndPassword at', loginTimestamp.current);
                
        // Always attempt sign in - maintenance mode will be checked after authentication in App.js
        // Add timeout for Safari (which can hang forever due to security restrictions)
        const signInPromise = signInWithEmailAndPassword(auth, email, password);
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('AUTH_TIMEOUT')), 10000)
        );
                
        const userCredential = await Promise.race([signInPromise, timeoutPromise]);
        const signInCompleteTime = Date.now();
        console.log(`🔐 [LOGIN-SUCCESS] Firebase sign-in completed in ${signInCompleteTime - loginTimestamp.current}ms, user:`, userCredential.user?.uid);
                
        // Register session token for single-session enforcement
        // This must happen before AuthContext sets up the listener
        try {
          await registerSession(userCredential.user.uid, email);
          console.log('🔐 [LOGIN-SESSION] Session token registered successfully');
        } catch (sessionError) {
          // Log prominently — if this fails, session enforcement is broken
          console.error('🚨 [LOGIN-SESSION] FAILED to register session token!', sessionError.code || sessionError.message);
          console.error('🚨 [LOGIN-SESSION] Single-session enforcement will NOT work. Check Firestore rules for activeSessions collection.');
        }
                
        // User status checking is now handled via Custom Claims
        // No need to check legacy paths - status verification happens in App.js
                
        // CRITICAL: Wait for claims to be available in the token, not just a fixed time
        setLoadingMessage(isSafari ? 'Safari: Verifying account...' : 'Verifying credentials...');
        console.log('🔐 [LOGIN-SUCCESS] Waiting for claims to be ready...');
                
        // Poll for claims with timeout (max 5 seconds)
        let claimsReady = false;
        let pollAttempts = 0;
        const maxAttempts = 20; // 20 * 250ms = 5 seconds max
                
        while (!claimsReady && pollAttempts < maxAttempts) {
          try {
            const idToken = await userCredential.user.getIdTokenResult(true); // Force refresh
            if (idToken.claims.role && idToken.claims.teamId) {
              console.log('🔐 [LOGIN-SUCCESS] Claims ready:', idToken.claims);
              claimsReady = true;
              break;
            }
          } catch (e) {
            console.log('🔐 [LOGIN-SUCCESS] Claims not ready yet, polling...');
          }
                    
          // Wait 250ms before next poll
          await new Promise(resolve => setTimeout(resolve, 250));
          pollAttempts++;
        }
                
        if (!claimsReady) {
          console.warn('⚠️ [LOGIN] Claims not ready after 5s, proceeding anyway');
        }
                
        // Use a delay to ensure AuthContext finishes processing before calling onLogin
        setLoadingMessage(isSafari ? 'Safari: Finishing up...' : 'Almost there...');
        console.log('🔐 [LOGIN-SUCCESS] Waiting briefly for auth state to settle...');
                
        // Brief final delay for AuthContext to update (claims already verified above)
        await new Promise(resolve => setTimeout(resolve, 300));
                
        console.log('🔐 [LOGIN-CALLBACK] Calling onLogin() after delay');
        onLogin();
        setLoading(false);
        setLoadingMessage('');
        console.log('🔐 [LOGIN-COMPLETE] Login flow finished');
      }
    } catch (error) {
      console.error('❌ [LOGIN-ERROR] Authentication error:', error);
      console.error('❌ [LOGIN-ERROR] Error code:', error.code, '| Message:', error.message);
            
      // User-friendly error messages (NO JARGON per Cursor Rules §1.3)
            
      // Handle timeout error (Safari security issue)
      if (error.message === 'AUTH_TIMEOUT') {
        setError('Safari is blocking the sign-in. Please use Chrome or Firefox, or go to Safari Settings → Privacy and disable "Prevent Cross-Site Tracking".');
        console.error('❌ [LOGIN-ERROR] Authentication timeout on Safari');
        return;
      }
            
      switch (error.code) {
        case 'auth/email-already-in-use':
          setError('An account with this email already exists. Please sign in instead.');
          break;
        case 'auth/user-not-found':
          setError(isTestEnv
            ? 'No account found with this email. Please double-check your spelling or contact an administrator for access.'
            : 'No account found with this email. Please double-check your spelling or sign up for a new account.');
          break;
        case 'auth/wrong-password':
          setError('Incorrect password. Please try again or click "Forgot Password?" below to reset it.');
          break;
        case 'auth/invalid-email':
          setError('Please enter a valid email address (e.g., you@company.com).');
          break;
        case 'auth/weak-password':
          setError('Password must be at least 6 characters long. Please choose a stronger password.');
          break;
        case 'auth/operation-not-allowed':
          setError('Sign-in is currently disabled. Please contact support for help.');
          break;
        case 'auth/network-request-failed':
          setError('Connection lost. Please check your internet connection and try again.');
          break;
        case 'auth/too-many-requests':
          setError('Too many unsuccessful login attempts. Please wait a few minutes and try again, or reset your password.');
          break;
        case 'auth/user-disabled':
          setError('This account has been disabled. Please contact your team lead or administrator for help.');
          break;
        default:
          // Provide helpful message even for unknown errors
          setError('Login failed. Please try again or contact support if this problem continues.');
          console.error('❌ [LOGIN-ERROR] Unhandled error code:', error.code);
      }
            
      // Clear any loading messages
      setLoadingMessage('');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSignupTier(null);
    setShowPlanSelection(false);
    setSelectedPlan('starter');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setTeamName('');
    setTeamLeadEmail('');
    setError('');
    setInvitationValidated(false);
    setInvitationData(null);
    // Reset forgot password state
    setShowForgotPassword(false);
    setForgotPasswordEmail('');
    setResetEmailSent(false);
  };

  // Forgot password functions
  const handleForgotPassword = () => {
    console.log('🔑 User clicked forgot password');
    setShowForgotPassword(true);
    setError('');
    // Pre-fill email if user has already entered it
    if (email.trim()) {
      setForgotPasswordEmail(email);
      console.log('🔑 Pre-filled email from login form');
    }
  };

  const handleBackToSignIn = () => {
    console.log('🔑 User returned to sign in from forgot password');
    setShowForgotPassword(false);
    setForgotPasswordEmail('');
    setResetEmailSent(false);
    setError('');
  };

  const handleSubmitPasswordReset = async (e) => {
    e.preventDefault();
        
    const emailToReset = forgotPasswordEmail.trim();
    console.log('🔑 Password reset form submitted');
        
    if (!emailToReset) {
      console.log('🔑 Password reset failed: Empty email');
      setError('Please enter your email address.');
      return;
    }

    // Check email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailToReset)) {
      console.log('🔑 Password reset failed: Invalid email format');
      setError('Please enter a valid email address.');
      return;
    }


    console.log('🔑 Sending password reset email to Firebase');
    setIsSubmittingReset(true);
    setError('');

    try {
      // Use shared auth instance from firebaseInit

      // Determine the correct URL based on environment
      const hostname = window.location.hostname;
      const isTestEnv = hostname.includes('treegenie-test') || 
                              hostname.includes('test.treegenie') ||
                              hostname === 'localhost';
      const resetUrl = isTestEnv 
        ? `https://${hostname}/action.html`
        : 'https://app.treegenie.ai/action.html';
            
      console.log('🔑 Environment:', isTestEnv ? 'TEST' : 'PRODUCTION');
      console.log('🔑 Reset URL:', resetUrl);
      console.log('🔑 Attempting to send reset email to:', emailToReset);
            
      // Configure action code settings to use our custom branded handler
      // Note: Domain must be authorized in Firebase Console > Authentication > Settings > Authorized domains
      const actionCodeSettings = {
        url: resetUrl,
        handleCodeInApp: false
      };
            
      await sendPasswordResetEmail(auth, emailToReset, actionCodeSettings);
            
      setResetEmailSent(true);
            
      console.log('✅ Password reset email sent successfully!');
      console.log('🔑 Check spam folder if not received within 2 minutes');
            
    } catch (error) {
      console.error('❌ Password reset error:', error.code, error.message);
            
      // Log specific error for debugging
      if (error.code === 'auth/user-not-found') {
        console.log('🔑 Note: No Firebase Auth account exists for this email');
      } else if (error.code === 'auth/invalid-email') {
        console.log('🔑 Note: Invalid email format');
      } else if (error.code === 'auth/too-many-requests') {
        console.log('🔑 Note: Too many reset attempts, try again later');
      }
            
      // Always show generic message for security (don't reveal if account exists)
      setError('If an account with that email exists, we\'ve sent a password reset link. Please check your email and spam folder.');
            
    } finally {
      setIsSubmittingReset(false);
    }
  };


  // Problem report functions
  const captureScreenshot = async () => {
    try {
      // For login page, capture the modal and some surrounding context
      const loginModal = document.querySelector('.bg-white.rounded-lg.p-4.max-w-md');
            
      if (loginModal) {
        // Capture modal with some padding for context
        const modalRect = loginModal.getBoundingClientRect();
        const padding = 50;
                
        const canvas = await html2canvas(document.body, {
          x: Math.max(0, modalRect.left - padding),
          y: Math.max(0, modalRect.top - padding),
          width: Math.min(window.innerWidth, modalRect.width + (padding * 2)),
          height: Math.min(window.innerHeight, modalRect.height + (padding * 2)),
          useCORS: true,
          allowTaint: true,
          scale: 0.6, // Better balance - readable but still compressed
          backgroundColor: '#f3f4f6',
        });
        return canvas.toDataURL('image/jpeg', 0.7); // Better quality for readability
      } else {
        // Fallback: capture center portion of screen
        const canvas = await html2canvas(document.body, {
          x: window.innerWidth * 0.1,
          y: window.innerHeight * 0.1,
          width: window.innerWidth * 0.8,
          height: window.innerHeight * 0.8,
          useCORS: true,
          allowTaint: true,
          scale: 0.5,
          backgroundColor: '#f3f4f6',
        });
        return canvas.toDataURL('image/jpeg', 0.7);
      }
    } catch (error) {
      console.error('Screenshot capture failed:', error);
      return null;
    }
  };

  const handleReportProblem = async () => {
    setIsCapturingData(true);
        
    try {
      // Capture screenshot
      const screenshot = await captureScreenshot();
      setCapturedScreenshot(screenshot);
            
      setShowFeedbackModal(true);
    } catch (error) {
      console.error('Error preparing problem report:', error);
      // Still show the modal even if screenshot fails
      setShowFeedbackModal(true);
    } finally {
      setIsCapturingData(false);
    }
  };

  const handleSubmitProblemReport = async (e) => {
    e.preventDefault();
        
    const description = feedbackDescription.trim();
        
    // Validation
    if (!description) {
      showWarning('Please add a description before submitting.');
      return;
    }

    if (description.length < 10) {
      showWarning('Please provide a bit more detail (at least 10 characters).');
      return;
    }

    if (description.length > 2000) {
      showWarning('Description is too long. Please keep it under 2000 characters.');
      return;
    }

    if (!db) {
      showError('Database not available. Please refresh the page and try again.');
      return;
    }

    // Basic rate limiting - check if user submitted recently
    const lastSubmission = localStorage.getItem('lastProblemReportSubmission');
    const now = Date.now();
    if (lastSubmission && (now - parseInt(lastSubmission)) < 30000) { // 30 seconds
      showWarning('Please wait 30 seconds before sending more feedback.');
      return;
    }

    setIsSubmittingReport(true);

    // Sanitized data for anonymous submission
    const reportData = {
      // Feedback info
      feedbackType: feedbackType,
      description: description,
      timestamp: new Date(),
      currentPage: 'Login',
            
      // User info (safe for login page)
      userEmail: email || 'Anonymous Login User',
            
      // Safe technical info (no sensitive data)
      userAgent: navigator.userAgent.substring(0, 200), // Limit length
      url: window.location.origin + window.location.pathname, // Remove query params
      referrer: document.referrer || 'Direct',
      appVersion: process.env.REACT_APP_VERSION || '0.1.1',
            
      // Login context (no sensitive data)
      loginMode: showForgotPassword ? 'forgot-password' : (isSignUp ? (signupTier ? `signup-${signupTier}` : 'signup-tier-selection') : 'signin'),
      hasInvitation: invitationValidated,
      maintenanceMode: maintenanceEnabled,
            
      // Screenshot (login modal with context)
      screenshot: capturedScreenshot || null,
            
      // Console logs from login page session
      consoleLogs: consoleHistory.current,
            
      // Application state (minimal for login page)
      applicationState: {
        loginMode: showForgotPassword ? 'forgot-password' : (isSignUp ? (signupTier ? `signup-${signupTier}` : 'signup-tier-selection') : 'signin'),
        hasError: !!error,
        errorMessage: error || null,
        maintenanceMode: maintenanceEnabled,
        invitationValidated: invitationValidated,
        forgotPasswordFlow: {
          isActive: showForgotPassword,
          emailSent: resetEmailSent
        }
      },
            
      // Debugging info
      debugInfo: {
        environment: process.env.NODE_ENV || 'Unknown',
        appId: getArtifactProjectId() || 'Unknown',
        hasError: !!error,
        errorMessage: error || null,
        timestamp: now,
        loginStates: {
          isSignUp,
          signupTier,
          invitationValidated,
          maintenanceEnabled
        }
      }
    };

    try {
      await addDoc(collection(db, 'problem-reports'), reportData);
            
      // Store submission timestamp for rate limiting
      localStorage.setItem('lastProblemReportSubmission', now.toString());
            
      // Reset form and close modal
      setFeedbackDescription('');
      setFeedbackType('bug');
      setCapturedScreenshot(null);
      setShowTechnicalDetails(false);
      setShowFeedbackModal(false);
            
      // Show success message
      showSuccess('Thank you for your feedback! We\'ll review it shortly.');
            
    } catch (error) {
      console.error('Error submitting feedback:', error);
      showError('Failed to send feedback. Please try again.');
    } finally {
      setIsSubmittingReport(false);
    }
  };

  // Render tier selection for signup
  // On test env, block self-service signup unless admin override or invite token is active
  if (isSignUp && !signupTier) {
    // Test environment without admin override AND no invite token: show restricted access message
    if (isTestEnv && !hasAdminOverride && !inviteToken && !urlInviteToken) {
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 max-w-md w-full mx-4 shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-800">Test Environment</h2>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={onClose}
                icon={
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                }
                aria-label="Close"
              />
            </div>

            <div className="p-4 bg-amber-50 border border-amber-200 rounded-md mb-4">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <div>
                  <h3 className="text-base font-semibold text-amber-800 mb-1">Signup Restricted</h3>
                  <p className="text-sm text-amber-700">
                    New account creation is disabled on the test environment. Only administrators can grant access.
                  </p>
                </div>
              </div>
            </div>

            <div className="text-sm text-gray-600 space-y-2 mb-4">
              <p><strong>Already have an account?</strong> Sign in below.</p>
              <p><strong>Need access?</strong> Ask an administrator to invite you. You'll receive an invitation link to create your account.</p>
            </div>

            <div className="text-center">
              <Button
                variant="primary"
                size="md"
                fullWidth
                onClick={() => {
                  setIsSignUp(false);
                  resetForm();
                }}
              >
                Go to Sign In
              </Button>
            </div>
          </div>
        </div>
      );
    }

    // Invite token present but validation failed — show the error, not the tier picker
    if (inviteToken || urlInviteToken) {
      return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 max-w-md w-full mx-4 shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Invitation Issue</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                icon={
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                }
                aria-label="Close"
              />
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md mb-4">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {!error && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md mb-4">
                <p className="text-sm text-amber-700">
                  This invitation link could not be verified. It may have expired or already been used.
                </p>
              </div>
            )}

            <div className="text-sm text-gray-600 mb-4">
              <p><strong>Already have an account?</strong> Sign in instead.</p>
              <p className="mt-1"><strong>Need a new link?</strong> Ask your team lead to send another invitation.</p>
            </div>

            <Button
              variant="primary"
              size="md"
              fullWidth
              onClick={() => {
                setIsSignUp(false);
                setInviteToken(null);
                setError('');
                resetForm();
              }}
            >
              Go to Sign In
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-4 max-w-md w-full mx-4 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-800">Choose Your Account Type</h2>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={onClose}
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              }
              aria-label="Close"
            />
          </div>

          <div className="space-y-4">
            <div 
              onClick={() => handleTierSelection('basic')}
              className="p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Agent</h3>
              <p className="text-gray-600 text-sm">
                                Join an existing team. You must be invited by a team lead before you can sign up.
              </p>
            </div>

            <div 
              onClick={() => handleTierSelection('team_lead')}
              className="p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Team Lead</h3>
              <p className="text-gray-600 text-sm">
                                Create and manage your own team. You can invite users and assign them to your team.
              </p>
            </div>
          </div>

          <div className="mt-6 text-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsSignUp(false);
                resetForm();
              }}
            >
                            Already have an account? Sign in
            </Button>
          </div>

          {/* Send Feedback Button - Bottom Left */}
          <div className="absolute bottom-4 left-4">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleReportProblem}
              disabled={isCapturingData}
              className={isCapturingData ? 'opacity-0' : ''}
              icon={
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              }
            >
              {isCapturingData ? 'Preparing...' : 'Send Feedback'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Render plan selection for team leads
  if (isSignUp && signupTier === 'team_lead' && showPlanSelection) {
    const paidPlans = ['starter', 'pro', 'pro_plus'];
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
        <div className="bg-white rounded-lg p-4 max-w-3xl w-full mx-2 shadow-sm">
          {/* Compact header */}
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  setShowPlanSelection(false);
                  setSignupTier(null);
                }}
                icon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                }
                aria-label="Back to account type"
              />
              <h2 className="text-xl font-bold text-gray-800">Choose Your Plan</h2>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={onClose}
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              }
              aria-label="Close"
            />
          </div>

          {/* Billing Toggle - Compact */}
          <div className="flex items-center justify-center mb-4">
            <span className={`text-sm ${billingInterval === 'month' ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingInterval(billingInterval === 'month' ? 'year' : 'month')}
              className={`mx-2 relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                billingInterval === 'year' ? 'bg-green-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                  billingInterval === 'year' ? 'translate-x-5' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-sm ${billingInterval === 'year' ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
              Yearly
            </span>
            <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
              Save 1 month!
            </span>
          </div>

          {/* Plan Cards - Compact */}
          <div className="grid md:grid-cols-3 gap-3">
            {paidPlans.map((planId) => {
              const plan = PLANS[planId];
              const isSelected = selectedPlan === planId;
              const price = billingInterval === 'year' ? plan.yearlyCost : plan.monthlyCost;
              const monthlyEquivalent = billingInterval === 'year' ? Math.round(plan.yearlyCost / 12) : plan.monthlyCost;
              
              return (
                <div
                  key={planId}
                  onClick={() => handlePlanSelection(planId)}
                  className={`relative p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    isSelected 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                  } ${plan.popular ? 'ring-2 ring-blue-400' : ''}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-2.5 left-1/2 transform -translate-x-1/2">
                      <span className="bg-blue-500 text-white text-xs font-medium px-2 py-0.5 rounded-full">
                        Popular
                      </span>
                    </div>
                  )}
                  
                  <h3 className="text-base font-bold text-gray-900 mt-1">{plan.name}</h3>
                  
                  <div className="my-2">
                    <span className="text-2xl font-bold text-gray-900">${monthlyEquivalent}</span>
                    <span className="text-gray-500 text-sm">/mo</span>
                    {billingInterval === 'year' && (
                      <span className="text-xs text-gray-400 ml-1">(${price}/yr)</span>
                    )}
                  </div>
                  
                  <ul className="space-y-1 mb-3 text-xs text-gray-600">
                    {plan.features.slice(0, 4).map((feature, idx) => (
                      <li key={idx} className="flex items-center">
                        <span className="text-green-500 mr-1">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <button
                    className={`w-full py-1.5 text-sm font-medium rounded transition-colors ${
                      isSelected 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePlanSelection(planId);
                    }}
                  >
                    {isSelected ? 'Selected ✓' : 'Select'}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Compact footer */}
          <div className="mt-3 flex items-center justify-between text-xs">
            <button
              type="button"
              onClick={() => {
                setShowPlanSelection(false);
                setSignupTier(null);
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              ← Back
            </button>
            <button
              type="button"
              onClick={() => {
                setIsSignUp(false);
                resetForm();
              }}
              className="text-blue-600 hover:text-blue-800"
            >
              Already have an account? Sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render loading state while validating invitation token
  if (validatingToken) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-4 max-w-md w-full mx-4 shadow-sm text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Validating Your Invitation</h2>
          <p className="text-gray-600">Please wait while we verify your invitation link...</p>
        </div>
      </div>
    );
  }

  // Render agent invitation validation (manual flow - no invite token)
  if (isSignUp && signupTier === 'basic' && !invitationValidated && !inviteToken) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-4 max-w-md w-full mx-4 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-800">Agent Signup</h2>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={onClose}
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              }
              aria-label="Close"
            />
          </div>

          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm text-blue-700">
              <strong>Agents must be invited:</strong> Enter your details below to validate your invitation.
            </p>
            <p className="text-xs text-blue-600 mt-2">
              <strong>Note:</strong> If validation fails, please verify both email addresses are correct or ask your team lead to re-invite you.
            </p>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleBasicEmailValidation(); }} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                Your Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="your@email.com"
                required
              />
            </div>

            <div>
              <label htmlFor="teamLeadEmail" className="block text-sm font-medium text-gray-700 mb-1">
                                Team Lead's Email Address
              </label>
              <input
                type="email"
                id="teamLeadEmail"
                value={teamLeadEmail}
                onChange={(e) => setTeamLeadEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="teamlead@company.com"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                                Enter the email address of the person who invited you to the team
              </p>
            </div>

            <InlineError message={error} />

            <Button
              type="submit"
              variant="primary"
              size="md"
              fullWidth
              loading={loading}
              disabled={loading}
            >
              {loading ? 'Validating...' : 'Validate Invitation'}
            </Button>
          </form>

          <div className="mt-6 text-center space-y-2">
            <Button
              variant="ghost"
              size="sm"
              fullWidth
              onClick={resetForm}
            >
                            ← Back to Account Type Selection
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsSignUp(false);
                resetForm();
              }}
            >
                            Already have an account? Sign in
            </Button>
          </div>

          {/* Send Feedback Button - Bottom Left */}
          <div className="absolute bottom-4 left-4">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleReportProblem}
              disabled={isCapturingData}
              className={isCapturingData ? 'opacity-0' : ''}
              icon={
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              }
            >
              {isCapturingData ? 'Preparing...' : 'Send Feedback'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Render main signup form
  // For team leads: show only after plan selection is done (showPlanSelection === false)
  // For agents: show after invitation is validated
  if (isSignUp && signupTier && ((signupTier === 'team_lead' && !showPlanSelection) || invitationValidated)) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
        <div className="bg-white rounded-lg p-4 max-w-md w-full mx-2 shadow-sm">
          {/* Compact header */}
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  if (signupTier === 'team_lead') {
                    setShowPlanSelection(true);
                  } else {
                    resetForm();
                  }
                }}
                icon={
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                }
                aria-label="Back"
              />
              <h2 className="text-xl font-bold text-gray-800">
                {signupTier === 'team_lead' ? 'Create Your Account' : 'Agent Signup'}
              </h2>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={onClose}
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              }
              aria-label="Close"
            />
          </div>

          {signupTier === 'basic' && invitationData && (
            <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-700">
                ✓ Joining team: <strong>{invitationData.teamName || invitationData.teamId || 'Pending'}</strong>
                {invitationData.invitedBy && (
                  <span className="text-xs text-green-600 block mt-1">
                    Invited by {invitationData.invitedBy}
                  </span>
                )}
              </p>
            </div>
          )}

          {signupTier === 'team_lead' && (
            <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-md flex items-center justify-between">
              <span className="text-sm text-blue-700">📋 <strong>{PLANS[selectedPlan]?.name} Plan</strong></span>
              <span className="text-xs text-blue-600">
                ${PLANS[selectedPlan]?.monthlyCost}/mo
              </span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            {/* First Name and Last Name */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="firstName" className="block text-xs font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Jane"
                  required
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-xs font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Smith"
                  required
                />
              </div>
            </div>

            {/* Email and Team Name in compact layout */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="email" className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    signupTier === 'basic' && inviteToken ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                  placeholder="you@email.com"
                  required
                  readOnly={signupTier === 'basic' && !!inviteToken}
                  title={signupTier === 'basic' && inviteToken ? 'Email is set from your invitation' : ''}
                />
              </div>
              {signupTier === 'team_lead' && (
                <div>
                  <label htmlFor="teamName" className="block text-xs font-medium text-gray-700 mb-1">Team Name</label>
                  <input
                    type="text"
                    id="teamName"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Your team"
                    required
                  />
                </div>
              )}
            </div>

            {/* Industry selection - Team Lead only */}
            {signupTier === 'team_lead' && (
              <div>
                <label htmlFor="industry" className="block text-xs font-medium text-gray-700 mb-1">Industry</label>
                <select
                  id="industry"
                  value={selectedIndustry}
                  onChange={(e) => {
                    const newId = e.target.value;
                    setSelectedIndustry(newId);
                    if (!industryDescriptionEdited) {
                      setIndustryDescription(getIndustryDescription(newId));
                    }
                  }}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  {getIndustryOptions().map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Industry description - Team Lead only */}
            {signupTier === 'team_lead' && (
              <div>
                <label htmlFor="industryDescription" className="block text-xs font-medium text-gray-700 mb-1">
                  Business Description <span className="text-gray-400 font-normal">(helps AI tailor your experience)</span>
                </label>
                <textarea
                  id="industryDescription"
                  value={industryDescription}
                  onChange={(e) => {
                    setIndustryDescription(e.target.value);
                    setIndustryDescriptionEdited(true);
                  }}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={selectedIndustry === 'other'
                    ? "Describe what your team does, e.g., 'Solar panel sales — scheduling in-home consultations with homeowners'"
                    : "Edit to customize how the AI understands your business"}
                  rows={2}
                  required={selectedIndustry === 'other'}
                />
              </div>
            )}

            {/* Password fields side by side */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="signup-password" className="block text-xs font-medium text-gray-700 mb-1">
                  Password <span className="text-gray-400 font-normal">(6+ chars)</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="signup-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-2 py-1.5 pr-8 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="••••••••"
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    aria-label="Toggle password visibility"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor="signup-confirmPassword" className="block text-xs font-medium text-gray-700 mb-1">
                  Confirm {password && confirmPassword && (password === confirmPassword ? <span className="text-green-600">✓</span> : <span className="text-red-500">✗</span>)}
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="signup-confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-2 py-1.5 pr-8 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="••••••••"
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    aria-label="Toggle password visibility"
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <InlineError message={error} />

            <Button
              type="submit"
              variant="primary"
              size="sm"
              fullWidth
              loading={loading}
              disabled={loading}
            >
              Create Account
            </Button>
          </form>

          {/* Compact footer */}
          <div className="mt-3 flex items-center justify-between text-xs">
            <button
              type="button"
              onClick={() => {
                if (signupTier === 'team_lead') {
                  // Go back to plan selection
                  setShowPlanSelection(true);
                } else {
                  // Go back to account type selection (for agents)
                  resetForm();
                }
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              ← Back
            </button>
            <button
              type="button"
              onClick={() => {
                setIsSignUp(false);
                resetForm();
              }}
              className="text-blue-600 hover:text-blue-800"
            >
              Already have an account? Sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render forgot password form
  if (showForgotPassword) {
    return (
      <>
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 max-w-md w-full mx-4 shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center space-x-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBackToSignIn}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  }
                  aria-label="Back to Sign In"
                />
                <h2 className="text-2xl font-bold text-gray-800">Reset Password</h2>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                icon={
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                }
                aria-label="Close"
              />
            </div>

            {resetEmailSent ? (
            // Success state
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                  <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Check Your Email</h3>
                <p className="text-sm text-gray-600 mb-4">
                                    If an account with <strong>{forgotPasswordEmail}</strong> exists, we've sent a password reset link. 
                                    Please check your email and spam folder.
                </p>
                <Button
                  variant="primary"
                  size="md"
                  fullWidth
                  onClick={handleBackToSignIn}
                >
                                    Back to Sign In
                </Button>
              </div>
            ) : (
            // Reset form
              <form onSubmit={handleSubmitPasswordReset} className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                                        Enter your email address and we'll send you a link to reset your password.
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">New Password Requirements:</h4>
                    <ul className="text-xs text-blue-700 space-y-1">
                      <li>• At least 6 characters long</li>
                      <li>• Mix of letters and numbers recommended</li>
                      <li>• Avoid common passwords like "password123"</li>
                      <li>• Choose something unique and secure</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <label htmlFor="resetEmail" className="block text-sm font-medium text-gray-700 mb-1">
                                        Email Address
                  </label>
                  <input
                    type="email"
                    id="resetEmail"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="your@email.com"
                    required
                    disabled={isSubmittingReset}
                  />
                </div>

                {error && (
                  <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  variant="primary"
                  size="md"
                  fullWidth
                  loading={isSubmittingReset}
                  disabled={isSubmittingReset}
                >
                                    Send Reset Email
                </Button>
              </form>
            )}

            {/* Send Feedback Button - Bottom Left */}
            <div className="absolute bottom-4 left-4">
              <Button
                variant="secondary"
                size="sm"
                onClick={handleReportProblem}
                disabled={isCapturingData}
                className={isCapturingData ? 'opacity-0' : ''}
                icon={
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                }
              >
                {isCapturingData ? 'Preparing...' : 'Send Feedback'}
              </Button>
            </div>
          </div>
        </div>

        {/* Send Feedback Modal */}
        {showFeedbackModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-sm max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <h2 className="text-lg font-semibold text-gray-900">Send Feedback</h2>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowFeedbackModal(false)}
                    disabled={isSubmittingReport}
                    icon={
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    }
                    aria-label="Close"
                  />
                </div>

                <form onSubmit={handleSubmitProblemReport} className="space-y-4">
                  {/* Feedback Type Selector */}
                  <div className="flex gap-2">
                    {[
                      { value: 'bug', label: 'Bug Report', icon: '🐛' },
                      { value: 'feature', label: 'Feature Request', icon: '💡' },
                      { value: 'general', label: 'General', icon: '💬' },
                    ].map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setFeedbackType(type.value)}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                          feedbackType === type.value
                            ? 'bg-blue-50 text-blue-700 border-2 border-blue-300'
                            : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                        }`}
                      >
                        <span>{type.icon}</span>
                        <span>{type.label}</span>
                      </button>
                    ))}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      {feedbackType === 'bug' 
                        ? 'What happened? What were you trying to do?' 
                        : feedbackType === 'feature' 
                          ? 'What would you like to see?' 
                          : 'What\'s on your mind?'}
                    </label>
                    <textarea
                      value={feedbackDescription}
                      onChange={(e) => setFeedbackDescription(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      rows={4}
                      placeholder={
                        feedbackType === 'bug'
                          ? 'Example: I clicked "Reset Password" but nothing happened...'
                          : feedbackType === 'feature'
                            ? 'Example: It would be great if I could...'
                            : 'Share your thoughts, questions, or suggestions...'
                      }
                      disabled={isSubmittingReport}
                      required
                    />
                  </div>

                  {/* Screenshot preview - compact */}
                  {capturedScreenshot && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md">
                      <img 
                        src={capturedScreenshot} 
                        alt="Screenshot attached" 
                        className="h-16 object-contain border border-gray-200 rounded"
                      />
                      <span className="text-xs text-gray-500">Screenshot attached automatically</span>
                    </div>
                  )}

                  {/* Collapsible technical details */}
                  <button
                    type="button"
                    onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
                    className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className={`w-3 h-3 transition-transform ${showTechnicalDetails ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    What info is included with this feedback?
                  </button>

                  {showTechnicalDetails && (
                    <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-md space-y-1">
                      <p>To help us understand and resolve issues, we automatically include:</p>
                      <ul className="ml-3 space-y-0.5 list-disc">
                        <li>Which page you were on</li>
                        <li>A screenshot of the current screen</li>
                        <li>Browser and app version details</li>
                        <li>Recent app activity logs (no passwords or personal data)</li>
                      </ul>
                    </div>
                  )}

                  <div className="flex justify-end space-x-3 pt-1">
                    <Button
                      variant="secondary"
                      size="md"
                      onClick={() => setShowFeedbackModal(false)}
                      disabled={isSubmittingReport}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      size="md"
                      loading={isSubmittingReport}
                      disabled={isSubmittingReport}
                    >
                      Send Feedback
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Render sign in form
  const signInFormContent = (
    <div className={`bg-white rounded-lg p-4 ${embedded ? 'w-full shadow-sm' : 'max-w-md w-full mx-4 shadow-sm'}`}>
      <div className={`flex ${embedded ? 'justify-center' : 'justify-between'} items-center mb-4`}>
        <h2 className="text-2xl font-bold text-gray-800">Sign In</h2>
        {!embedded && onClose && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            icon={
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            }
            aria-label="Close"
          />
        )}
      </div>

      {/* Test Environment Restricted Banner */}
      {isTestEnv && (
        <div className="mb-4 p-3 bg-amber-50 border-l-4 border-amber-400 rounded">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-amber-800">
                Test Environment — Restricted Access
              </p>
              <p className="mt-1 text-xs text-amber-700">
                New account creation is disabled. Sign in with an existing account or contact an admin for access.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Safari Warning - Show BEFORE form */}
      {isSafari && (
        <div className="mb-4 p-3 bg-amber-50 border-l-4 border-amber-400 rounded">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-amber-800">
                                        Safari Compatibility Issue
              </p>
              <p className="mt-1 text-xs text-amber-700">
                                        Safari may block sign-in due to security settings. For best experience, please use <strong>Chrome</strong> or <strong>Firefox</strong>.
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                Email
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(''); }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="your@email.com"
            required
            autoComplete="username"
          />
        </div>

        <div>
          <label htmlFor="signin-password" className="block text-sm font-medium text-gray-700 mb-1">
                                Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              id="signin-password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="••••••••"
              required
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
              aria-label="Toggle password visibility"
              tabIndex={-1}
            >
              {showPassword ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <InlineError message={error} />

        <Button
          type="submit"
          variant="primary"
          size="md"
          fullWidth
          loading={loading}
          disabled={loading}
        >
                            Sign In
        </Button>
      </form>

      <div className="mt-4 text-xs text-gray-500 text-center">
        <p>Passwords must be at least 6 characters long</p>
      </div>

      <div className="mt-6 text-center space-y-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleForgotPassword}
          className="underline"
        >
                        Forgot your password?
        </Button>
                    
        {(!isTestEnv || hasAdminOverride) && (
          <>
            <div className="text-gray-400 text-sm">or</div>
                    
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsSignUp(true);
                resetForm();
              }}
            >
              Don't have an account? Sign up
            </Button>
          </>
        )}
      </div>

      {/* Send Feedback Button - Bottom Left (hidden in embedded mode) */}
      {!embedded && (
        <div className="absolute bottom-4 left-4">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleReportProblem}
            disabled={isCapturingData}
            className={isCapturingData ? 'opacity-0' : ''}
            icon={
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            }
          >
            {isCapturingData ? 'Preparing...' : 'Send Feedback'}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {embedded ? (
        signInFormContent
      ) : (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          {signInFormContent}
        </div>
      )}

      {/* Send Feedback Modal */}
      {showFeedbackModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-sm max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <h2 className="text-lg font-semibold text-gray-900">Send Feedback</h2>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFeedbackModal(false)}
                  disabled={isSubmittingReport}
                  icon={
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  }
                  aria-label="Close"
                />
              </div>

              <form onSubmit={handleSubmitProblemReport} className="space-y-4">
                {/* Feedback Type Selector */}
                <div className="flex gap-2">
                  {[
                    { value: 'bug', label: 'Bug Report', icon: '🐛' },
                    { value: 'feature', label: 'Feature Request', icon: '💡' },
                    { value: 'general', label: 'General', icon: '💬' },
                  ].map((type) => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFeedbackType(type.value)}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                        feedbackType === type.value
                          ? 'bg-blue-50 text-blue-700 border-2 border-blue-300'
                          : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      <span>{type.icon}</span>
                      <span>{type.label}</span>
                    </button>
                  ))}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {feedbackType === 'bug' 
                      ? 'What happened? What were you trying to do?' 
                      : feedbackType === 'feature' 
                        ? 'What would you like to see?' 
                        : 'What\'s on your mind?'}
                  </label>
                  <textarea
                    value={feedbackDescription}
                    onChange={(e) => setFeedbackDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={4}
                    placeholder={
                      feedbackType === 'bug'
                        ? 'Example: I clicked "Sign In" but nothing happened...'
                        : feedbackType === 'feature'
                          ? 'Example: It would be great if I could...'
                          : 'Share your thoughts, questions, or suggestions...'
                    }
                    disabled={isSubmittingReport}
                    required
                  />
                </div>

                {/* Screenshot preview - compact */}
                {capturedScreenshot && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-md">
                    <img 
                      src={capturedScreenshot} 
                      alt="Screenshot attached" 
                      className="h-16 object-contain border border-gray-200 rounded"
                    />
                    <span className="text-xs text-gray-500">Screenshot attached automatically</span>
                  </div>
                )}

                {/* Collapsible technical details */}
                <button
                  type="button"
                  onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
                  className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className={`w-3 h-3 transition-transform ${showTechnicalDetails ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  What info is included with this feedback?
                </button>

                {showTechnicalDetails && (
                  <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-md space-y-1">
                    <p>To help us understand and resolve issues, we automatically include:</p>
                    <ul className="ml-3 space-y-0.5 list-disc">
                      <li>Which page you were on</li>
                      <li>A screenshot of the current screen</li>
                      <li>Browser and app version details</li>
                      <li>Recent app activity logs (no passwords or personal data)</li>
                    </ul>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-1">
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={() => setShowFeedbackModal(false)}
                    disabled={isSubmittingReport}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    size="md"
                    loading={isSubmittingReport}
                    disabled={isSubmittingReport}
                  >
                    Send Feedback
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </>
  );
};

export default Login;
